import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  XCircle
} from 'lucide-react';
import { useWallet } from '../../../../context/wallet/WalletContext';
import {
  getBusinessTitle,
  getBusinessLocation,
  extractBookingId
} from '../../utils/walletHelpers';
import { formatDate, format24HourTime } from '../../../../utils/formatters';

const getStatusIcon = (status) => {
  switch (status?.toUpperCase()) {
    case 'SUCCESS':
    case 'COMPLETED':
    case 'PROCESSED':
      return <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />;
    case 'PENDING':
    case 'PROCESSING':
      return <Clock size={13} className="text-amber-600 shrink-0" />;
    case 'FAILED':
      return <XCircle size={13} className="text-rose-600 shrink-0" />;
    default:
      return null;
  }
};

const WalletTransactionMobileCard = ({ tx }) => {
  const { expandedTxId, toggleExpandTx, bookings } = useWallet();

  const isCredit = tx.type === 'CREDIT';
  const isExpanded = expandedTxId === tx._id;
  const bookingId = extractBookingId(tx.description, tx.referenceId);
  const booking = bookings.find((b) => b.booking_id === bookingId || b._id === tx.referenceId);

  return (
    <div className="p-3.5 sm:p-4 transition-colors">
      <div
        onClick={() => toggleExpandTx(tx._id)}
        className="flex items-start justify-between gap-2.5 cursor-pointer"
      >
        <div className="flex items-start gap-2.5 min-w-0">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border shadow-2xs mt-0.5 ${
              isCredit
                ? 'bg-emerald-50 border-emerald-100 text-emerald-600'
                : 'bg-rose-50 border-rose-100 text-rose-600'
            }`}
          >
            {isCredit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-slate-900 text-xs truncate">
                {isCredit ? 'Commission Earned' : 'Wallet Withdrawal'}
              </span>
              <span
                className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md ${
                  isCredit ? 'bg-emerald-100/60 text-emerald-800' : 'bg-slate-100 text-slate-700'
                }`}
              >
                {isCredit
                  ? 'Commission'
                  : tx.description?.toLowerCase().includes('bank')
                  ? 'Bank'
                  : 'UPI'}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 truncate mt-0.5">
              {tx.description || (isCredit ? 'Partner Booking Commission' : 'Wallet Redemption')}
            </p>
            <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-400">
              <span>
                {formatDate(tx.createdAt)} {format24HourTime(tx.createdAt)}
              </span>
              <span>•</span>
              <span
                className={`inline-flex items-center gap-1 font-medium ${
                  (tx.status || '').toUpperCase() === 'SUCCESS' ||
                  (tx.status || '').toUpperCase() === 'COMPLETED'
                    ? 'text-emerald-600'
                    : 'text-amber-600'
                }`}
              >
                {getStatusIcon(tx.status)}
                <span className="capitalize">{tx.status || 'Completed'}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end shrink-0">
          <span className={`text-sm font-bold ${isCredit ? 'text-emerald-600' : 'text-slate-800'}`}>
            {isCredit ? '+' : '-'}₹{(tx.amount || 0).toLocaleString('en-IN')}
          </span>
          <div className="w-5 h-5 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center mt-1.5">
            {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </div>
        </div>
      </div>

      {/* Expanded Mobile Details */}
      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-xs animate-in fade-in duration-150">
          {isCredit && booking ? (
            <div className="bg-slate-50 rounded-2xl p-3 space-y-2 border border-slate-100 text-slate-700">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">Guest:</span>
                <b>{booking.guest_name || booking.guestName || 'App User'}</b>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">Partner:</span>
                <b className="truncate max-w-[180px]">{getBusinessTitle(booking)}</b>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">City / Type:</span>
                <span>
                  {getBusinessLocation(booking)} · <b className="capitalize">{booking.type}</b>
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">Total Bill:</span>
                <span>₹{(booking.final_bill_amount || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center justify-between text-[11px] pt-1.5 border-t border-slate-200/60">
                <span className="text-slate-500">Booking ID:</span>
                <span className="font-mono text-[#067978] font-bold lowercase">{booking.booking_id}</span>
              </div>
            </div>
          ) : (
            <div className="bg-slate-50 rounded-2xl p-3 space-y-1.5 border border-slate-100 text-slate-700">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">Details:</span>
                <span className="truncate max-w-[180px]">{tx.description}</span>
              </div>
              {tx.razorpayPayoutId && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">Payout ID:</span>
                  <span className="font-mono text-slate-600">{tx.razorpayPayoutId}</span>
                </div>
              )}
              {tx.referenceId && (
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">Ref Token:</span>
                  <span className="font-mono text-slate-600 truncate max-w-[150px]">{tx.referenceId}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WalletTransactionMobileCard;
